/**
 * Design tokens — single source of truth for sizing, spacing, shape, motion, and color
 * across all shared UI components (Button, IconButton, Input, Select, …).
 *
 * Import individual groups:
 *   import { size, radius, fontFamily, transition, color } from "../tokens";
 */

/**
 * Size scale shared by interactive components.
 * height    — element height in px (buttons, inputs, selects align on this axis)
 * paddingX  — horizontal padding in px for text-bearing components
 * fontSize  — label / value font size in px
 * iconSize  — icon stroke width target in px (pass to lucide-react's `size` prop)
 * iconGap   — gap between icon and label in px
 */
export const size = {
  extraSmall: { height: 24, paddingX: 8,  fontSize: 13, iconSize: 12, iconGap: 4  },
  small:      { height: 32, paddingX: 12, fontSize: 14, iconSize: 14, iconGap: 6  },
  medium:     { height: 40, paddingX: 16, fontSize: 16, iconSize: 18, iconGap: 8  },
  large:      { height: 48, paddingX: 24, fontSize: 18, iconSize: 22, iconGap: 10 },
};

/**
 * Border-radius scale.
 * sm   — tight rounding for inputs, buttons, chips (default interactive element)
 * md   — card corners, popovers, dropdowns
 * lg   — modals, panels, larger surfaces
 * xl   — prominent cards or hero containers
 * full — pill shapes, avatar thumbnails
 */
export const radius = {
  none: 0,
  sm:   4,
  md:   8,
  lg:   12,
  xl:   16,
  full: 9999,
};

/**
 * Color palette — raw named values.
 * Prefer the semantic `color` layer below in components; use palette directly
 * only when you need a specific shade that has no semantic name.
 */
export const palette = {
  brand: {
    dark:  "#0C0E29", // primary brand dark
    blue:  "#4654FB", // vivid brand blue
    navy:  "#1D3557", // deep navy accent
  },
  gray: {
    50:  "#F9F9F9",
    100: "#F5F5F5",
    200: "#EDEDED",
    300: "#DCDCDC",
    400: "#BDBDBD",
    500: "#9ca3af",
    600: "#6b7280",
    700: "#495057",
    800: "#1f2937",
    900: "#0C0E29",
  },
  red: {
    light: "#fff5f5",
    base:  "#D84949",
    dark:  "#C54242",
  },
  green: {
    light: "#E2F8E0",
    base:  "#22c55e",
    dark:  "#3AB037",
  },
  amber: {
    base: "#f59e0b",
  },
  white: "#FFFFFF",
  black: "#000000",
};

/**
 * Semantic color tokens — intent-based aliases over the palette.
 * Use these in components so color meaning stays readable.
 *
 *   color.text.primary      → heading / body text
 *   color.surface.card      → card / panel background
 *   color.border.default    → card edges, dividers
 *   color.status.success    → active / connected state
 */
export const color = {
  text: {
    primary:   palette.gray[900],   // #0C0E29 — headings, body
    secondary: palette.gray[600],   // #6b7280 — hints, captions
    muted:     palette.gray[500],   // #9ca3af — placeholders, disabled labels
    inverse:   palette.white,       // white text on dark backgrounds
  },
  surface: {
    card:     palette.white,        // card / panel background
    subtle:   palette.gray[50],     // section backgrounds, zebra rows
    hover:    palette.gray[200],    // hover state fills
    disabled: palette.gray[300],    // disabled input / button background
  },
  border: {
    default:     palette.gray[200], // #EDEDED — card edges, dividers
    strong:      palette.gray[300], // #DCDCDC — separators, form outlines
    interactive: palette.gray[900], // #0C0E29 — focused / active borders
  },
  status: {
    success:  palette.green.base,   // #22c55e — active, connected
    error:    palette.red.base,     // #D84949 — destructive, error
    warning:  palette.amber.base,   // #f59e0b — registered, pending
    inactive: palette.gray[500],    // #9ca3af — offline, deregistered
  },
  shadow: {
    sm: "rgba(0, 0, 0, 0.05)",
    md: "rgba(0, 0, 0, 0.10)",
    lg: "rgba(0, 0, 0, 0.20)",
  },
};

/** Font-family definitions. */
export const fontFamily = {
  base: "Lato, sans-serif",
  mono: "'Courier New', Courier, monospace",
};

/**
 * Transition duration + easing pairs.
 * Use `fast` for micro-interactions (hover state, icon swap).
 * Use `base` for most animations (border-color change, opacity fade).
 * Use `slow` for layout shifts or larger reveals.
 */
export const transition = {
  fast: "0.15s ease",
  base: "0.2s ease",
  slow: "0.3s ease",
};
